import { useState, type FormEvent, type KeyboardEvent } from 'react';

import { TreeReplacementError } from '../../../../api/tree-replacements/errors';
import type { TreeSpeciesOption } from '../../../../api/workshops';
import { isSubmitShortcut } from '../../../../utils/submit-shortcut';
import { DANGER_RED } from '../../primitives';

import {
  formErrorsFromServer,
  validateTreeReplacementForm,
  type TreeReplacementFormErrors,
  type TreeReplacementFormValues,
} from './tree-replacement-form';
import { TreeReplacementFormFields } from './TreeReplacementFormFields';

/** The add and edit form body: validates locally, then hands valid values to
 *  `onSubmit`. The modal's footer submits it through `formId`, as does Ctrl+Enter
 *  (⌘+Enter on a Mac) from any field. */
export function TreeReplacementForm({
  formId,
  initial,
  saving,
  error,
  storedSpecies = null,
  onSubmit,
}: {
  formId: string;
  initial: TreeReplacementFormValues;
  saving: boolean;
  error: Error | null;
  /** The species saved on the replacement, so an inactive one can still be shown. */
  storedSpecies?: TreeSpeciesOption | null;
  onSubmit: (values: TreeReplacementFormValues) => void;
}) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<TreeReplacementFormErrors>({});

  const serverErrors =
    error instanceof TreeReplacementError ? formErrorsFromServer(error.fieldErrors) : {};
  const hasServerFieldErrors = Object.keys(serverErrors).length > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const found = validateTreeReplacementForm(values, { includeContact: true });
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit(values);
  };

  const submitOnShortcut = (e: KeyboardEvent<HTMLFormElement>) => {
    if (!isSubmitShortcut(e)) return;
    e.preventDefault();
    e.currentTarget.requestSubmit();
  };

  return (
    <form id={formId} onSubmit={submit} onKeyDown={submitOnShortcut} noValidate>
      <TreeReplacementFormFields
        values={values}
        errors={{ ...serverErrors, ...errors }}
        includeContact
        storedSpecies={storedSpecies}
        onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
      />
      {error && (
        <div role="alert" style={{ color: DANGER_RED, fontSize: 13, marginTop: 12 }}>
          {hasServerFieldErrors ? 'Check the highlighted fields and try again.' : error.message}
        </div>
      )}
    </form>
  );
}
